import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import InputCheckbox from '../../../../../../components/Inputs/Checkbox';
import { BenchmarkResultsInputRequest } from '../../../../../../generated/model/benchmarkResultsInputRequest';
import { ResultsInputsHookState } from '../useResultsInputs';
import { adjustmentValues } from './AdjustmentsValues';
import InterestRatesModal from './InterestRatesModal';

interface OwnProps {
  resultsInputs: ResultsInputsHookState;
}

const AdjustmentsInput: React.FC<OwnProps> = ({ resultsInputs }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const {
    adjustments,
    profitLevelIndicator,
    manufacturerOrDistributor,
    handleAdjustmentsChange,
    handleManufacturerOrDistributorChange,
  } = resultsInputs;

  const [showInterestRatesModal, setShowInterestRatesModal] = useState(false);

  const onInterestRatesModalClose = (hasChanges: boolean) => {
    if (hasChanges) {
      resultsInputs.submitForm(
        resultsInputs.profitLevelIndicator,
        resultsInputs.indicatorType,
        resultsInputs.yearRange,
        resultsInputs.adjustments,
        resultsInputs.fiscalYearAlignment,
        resultsInputs.manufacturerOrDistributor,
      );
    }
    setShowInterestRatesModal(false);
  };

  return (
    <>
      <FormControl component="fieldset" className={styles.control}>
        <FormLabel>{t('benchmark:benchmarkResults.adjustments.adjustments')}:</FormLabel>

        <div className={styles.adjustments}>
          {adjustmentValues
            .filter((type) => type.excludedIndicators.indexOf(profitLevelIndicator) === -1)
            .map((type) => (
              <InputCheckbox
                label={type.label}
                name={type.value}
                key={type.value}
                defaultValue={adjustments.indexOf(type.value) >= 0}
                onChange={(value) => {
                  handleAdjustmentsChange(type.value, value);
                }}
              />
            ))}
        </div>
      </FormControl>
      <FormControl component="fieldset" className={styles.control}>
        <Button
          onClick={() => {
            setShowInterestRatesModal(true);
          }}
          color="primary"
          variant="contained"
          className={styles.irButton}
        >
          {t('benchmark:benchmarkResults.interestRates.overrideInterestRates')}
        </Button>
      </FormControl>
      <InterestRatesModal
        show={showInterestRatesModal}
        yearRange={resultsInputs.yearRange}
        handleClose={onInterestRatesModalClose}
      />

      {adjustments?.length !== 0 &&
        (profitLevelIndicator === 'RETURN_ON_ASSETS' ||
          profitLevelIndicator === 'RETURN_ON_CAPITAL_EMPLOYED') && (
          <FormControl component="fieldset" className={styles.control}>
            <RadioGroup
              row
              aria-label="manufacturerOrDistributorType"
              name="manufacturerOrDistributorTypeRadioGroup"
              value={manufacturerOrDistributor}
              onChange={handleManufacturerOrDistributorChange}
            >
              <FormControlLabel
                value={'MANUFACTURER' as BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum}
                control={<Radio size="small" color="primary" />}
                label={t('benchmark:benchmarkResults.adjustments.manufacturer')}
              />
              <FormControlLabel
                value={'DISTRIBUTOR' as BenchmarkResultsInputRequest.ManufacturerOrDistributorEnum}
                control={<Radio size="small" color="primary" />}
                label={t('benchmark:benchmarkResults.adjustments.distributor')}
              />
            </RadioGroup>
          </FormControl>
        )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  control: {
    paddingLeft: '12px',
    paddingTop: '9px',
    '& .MuiCheckbox-root': {
      padding: '7px',
    },
  },
  irButton: {
    maxWidth: '250px',
    minWidth: '100px',
  },
  adjustments: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default AdjustmentsInput;
